import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import "../../styles/components/_article.scss";

function normalizeHref(value) {
    if (!value) return "";
    const raw = String(value).trim();
    if (!raw) return "";
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    if (raw.startsWith("mailto:")) return raw;
    if (raw.startsWith("/")) return raw;
    if (raw.startsWith("#")) return raw;
    if (raw.startsWith("help/")) return `/${raw}`;
    return `/${raw}`;
}

function LinkOrAnchor({ href, className, children, ...rest }) {
    const normalized = normalizeHref(href);
    const isExternal =
        normalized.startsWith("http://") ||
        normalized.startsWith("https://") ||
        normalized.startsWith("mailto:");

    if (!normalized) {
        return (
            <span className={className} {...rest}>
                {children}
            </span>
        );
    }

    return isExternal ? (
        <a
            href={normalized}
            className={className}
            target={normalized.startsWith("http") ? "_blank" : undefined}
            rel={normalized.startsWith("http") ? "noreferrer" : undefined}
            {...rest}
        >
            {children}
        </a>
    ) : (
        <Link to={normalized} className={className} {...rest}>
            {children}
        </Link>
    );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function stripHtml(html) {
    if (!html) return "";
    return html
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function formatUpdatedAt(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat("en-GB", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date);
}

// ─── On-this-page TOC ─────────────────────────────────────────────────────────
function TableOfContents({ sections }) {
    const [activeId, setActiveId] = useState(null);

    useEffect(() => {
        if (!sections?.length) return;
        const ids = sections.map((s) => s.id).filter(Boolean);
        const observers = new Map();

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveId(entry.target.id);
                });
            },
            { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
        );

        ids.forEach((id) => {
            const el = document.getElementById(id);
            if (el) {
                io.observe(el);
                observers.set(id, el);
            }
        });

        return () => io.disconnect();
    }, [sections]);

    if (!sections?.length) return null;

    return (
        <nav className="at-toc" aria-label="On this page">
            <p className="at-toc__heading">On this page</p>
            <ul className="at-toc__list">
                {sections.map((sec) => (
                    <li key={sec.id || sec.title}>
                        <a
                            href={`#${sec.id}`}
                            className={`at-toc__link${activeId === sec.id ? " is-active" : ""}`}
                        >
                            {sec.title}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

// ─── Back-to-top button ───────────────────────────────────────────────────────
function BackToTop() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const handler = () => setVisible(window.scrollY > 400);
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, []);

    if (!visible) return null;

    // return (
    //     <button
    //         type="button"
    //         className="at-back-top"
    //         onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    //         aria-label="Back to top"
    //     >
    //         <IconChevUp />
    //     </button>
    // );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ArticleTemplate({
    article,
    breadcrumbs = [],
    backHref,
    backLabel,
    showSupport = true,
    supportEmail = "support@moozvideointerview.com",
}) {
    const location = useLocation();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [location.pathname]);

    const readingTime = useMemo(() => {
        if (article?.estimatedReadTime) {
            if (typeof article.estimatedReadTime === "number") {
                return `${Math.max(1, Math.round(article.estimatedReadTime))} min read`;
            }
            if (typeof article.estimatedReadTime === "string") {
                return article.estimatedReadTime.includes("min")
                    ? article.estimatedReadTime
                    : `${article.estimatedReadTime} min read`;
            }
        }
        const text = [
            article?.intro,
            article?.body,
            ...(article?.sections?.map((sec) => sec.body) || []),
        ]
            .filter(Boolean)
            .map(stripHtml)
            .join(" ");

        const words = text.trim().split(/\s+/).filter(Boolean).length;
        const seconds = (words / 200) * 60;
        let minutes;

        if (seconds < 30) minutes = 1;
        else {
            minutes = Math.floor(seconds / 60);
            if (seconds % 60 >= 30) minutes += 1;
        }

        return `${Math.max(1, minutes)} min read`;
    }, [article]);

    const updatedAtLabel = formatUpdatedAt(article?.updatedAt);

    // ── Not found ──────────────────────────────────────────────────────────────
    if (!article) {
        return (
            <div className="at-page">
                <div className="at-not-found">
                    <p className="at-not-found__code">Article not found</p>
                    <h1 className="at-not-found__title">
                        This page doesn't exist or has moved.
                    </h1>
                    {backHref && (
                        <LinkOrAnchor
                            href={backHref}
                            className="at-btn at-btn--primary"
                        >
                            {backLabel || "Go back"}
                        </LinkOrAnchor>
                    )}
                </div>
            </div>
        );
    }

    const hasSections = article.sections?.some(
        (s) => s.body || s.bullets?.length || s.steps?.length || s.callout
    );

    return (
        <div className="at-page">
            {breadcrumbs?.length > 0 && <Breadcrumbs items={breadcrumbs} />}

            <div className="at-layout">
                {/* ── Sidebar ── */}
                <aside className="at-sidebar" aria-label="Article navigation">
                    <TableOfContents sections={article.sections} />

                    {article.related?.length > 0 && (
                        <div className="at-related">
                            <p className="at-related__heading">Related</p>
                            <ul className="at-related__list">
                                {article.related.map((r) => (
                                    <li key={r.href || r.path || r.title}>
                                        <LinkOrAnchor
                                            href={r.href || r.path}
                                            className="at-related__link"
                                        >
                                            <span className="at-related__text">
                                                <span className="at-related__link-title">
                                                    {r.title}
                                                </span>
                                                {r.description && (
                                                    <span className="at-related__link-desc">
                                                        {r.description}
                                                    </span>
                                                )}
                                            </span>
                                            <span
                                                className="at-icon at-icon--arrow"
                                                aria-hidden="true"
                                            />
                                        </LinkOrAnchor>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* {showSupport && (
                        <div className="at-support-card">
                            <IconMail />
                            <div>
                                <p className="at-support-card__title">
                                    Need more help?
                                </p>
                                <a
                                    href={`mailto:${supportEmail}`}
                                    className="at-support-card__link"
                                >
                                    Contact support
                                </a>
                            </div>
                        </div>
                    )} */}
                </aside>

                {/* ── Article body ── */}
                <div className="at-body">
                    <header className="at-header">
                        {/* Meta row */}
                        <div className="at-meta">
                            {article.category && (
                                <span className="at-meta__pill">
                                    {article.category}
                                </span>
                            )}
                            <span className="at-meta__item">
                                <span
                                    className="at-icon at-icon--clock"
                                    aria-hidden="true"
                                />
                                {readingTime}
                            </span>
                            {updatedAtLabel && (
                                <span className="at-meta__item">
                                    <span
                                        className="at-icon at-icon--calendar"
                                        aria-hidden="true"
                                    />
                                    Updated {updatedAtLabel}
                                </span>
                            )}
                        </div>

                        <h1 className="at-title">{article.title}</h1>

                        {article.introHtml ? (
                            <div
                                className="at-intro rich-text"
                                dangerouslySetInnerHTML={{
                                    __html: article.introHtml,
                                }}
                            />
                        ) : article.intro ? (
                            <p className="at-intro">{article.intro}</p>
                        ) : null}

                        {(article.primaryCta || article.secondaryCta) && (
                            <div className="at-actions">
                                {article.primaryCta && (
                                    <LinkOrAnchor
                                        href={article.primaryCta.href}
                                        className="at-btn at-btn--primary"
                                    >
                                        {article.primaryCta.label}
                                    </LinkOrAnchor>
                                )}
                                {article.secondaryCta && (
                                    <LinkOrAnchor
                                        href={article.secondaryCta.href}
                                        className="at-btn at-btn--secondary"
                                    >
                                        {article.secondaryCta.label}
                                    </LinkOrAnchor>
                                )}
                            </div>
                        )}
                    </header>

                    <main className="at-content">
                        {hasSections ? (
                            <div className="at-sections">
                                {article.sections.map((sec, idx) => (
                                    <section
                                        key={`${sec.id}-${idx}`}
                                        id={sec.id}
                                        className="at-section"
                                    >
                                        <h2 className="at-section__heading">
                                            {sec.title}
                                        </h2>

                                        {sec.body && (
                                            <div
                                                className="rich-text"
                                                dangerouslySetInnerHTML={{
                                                    __html: sec.body,
                                                }}
                                            />
                                        )}

                                        {sec.bullets?.length > 0 && (
                                            <ul className="at-bullet-list">
                                                {sec.bullets.map((b, i) => (
                                                    <li key={i}>{b}</li>
                                                ))}
                                            </ul>
                                        )}

                                        {sec.steps?.length > 0 && (
                                            <ol className="at-step-list">
                                                {sec.steps.map((s, i) => (
                                                    <li
                                                        key={i}
                                                        className="at-step"
                                                    >
                                                        <div
                                                            className="at-step__counter"
                                                            aria-hidden="true"
                                                        >
                                                            {i + 1}
                                                        </div>
                                                        <div className="at-step__content">
                                                            {s.title && (
                                                                <p className="at-step__title">
                                                                    {s.title}
                                                                </p>
                                                            )}
                                                            {s.body && (
                                                                <p className="at-step__body">
                                                                    {s.body}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ol>
                                        )}

                                        {sec.callout && (
                                            <div className="at-callout">
                                                {sec.callout.title && (
                                                    <p className="at-callout__title">
                                                        {sec.callout.title}
                                                    </p>
                                                )}
                                                <p className="at-callout__body">
                                                    {sec.callout.body}
                                                </p>
                                            </div>
                                        )}
                                    </section>
                                ))}
                            </div>
                        ) : (
                            <div
                                className="rich-text"
                                dangerouslySetInnerHTML={{
                                    __html: article.body || "",
                                }}
                            />
                        )}
                    </main>

                    {/* Footer: related (mobile only) + support */}
                    <footer className="at-footer">
                        {article.related?.length > 0 && (
                            <div className="at-related at-related--mobile">
                                <p className="at-related__heading">
                                    Related articles
                                </p>
                                <ul className="at-related__list">
                                    {article.related.map((r) => (
                                        <li key={r.href || r.path || r.title}>
                                            <LinkOrAnchor
                                                href={r.href || r.path}
                                                className="at-related__link"
                                            >
                                                <span className="at-related__text">
                                                    <span className="at-related__link-title">
                                                        {r.title}
                                                    </span>
                                                </span>
                                                <span
                                                    className="at-icon at-icon--arrow"
                                                    aria-hidden="true"
                                                />
                                            </LinkOrAnchor>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {showSupport && (
                            <div className="at-support-inline">
                                <span
                                    className="at-icon at-icon--mail"
                                    aria-hidden="true"
                                />
                                <p>
                                    Still need help?{" "}
                                    <a href={`mailto:${supportEmail}`}>
                                        Reach out
                                    </a>{" "}
                                    and we'll get you sorted.
                                </p>
                            </div>
                        )}
                    </footer>
                </div>
            </div>

            <BackToTop />
        </div>
    );
}
